<template>
  <div class="s-setting-container">
    <div class="main-header">
      <div>Company Information</div>
      <SaveCancelSection
        v-if="adminRole === 'admin' || adminRole === 'manager'"
        :cancel="resetPartnerConfig"
        :save="updatePartnerConfig"
        :disabled="!changeCheck"
      />
    </div>
    <PartnerInfo
      :partnerName="corporationName"
      :displayName="displayName"
      :website="website"
      :supportPhone="supportPhone"
      :supportPhoneCo="supportPhoneCo"
      :supportEmail="supportEmail"
      :change="change"
      :phone="phone"
      :phoneCountry="phoneCountry"
      :screen="screen"
      :onChange="handleChange"
      :errorObj="errorObj"
    />
    <PartnerEin :handleChange="handleChange" :ein="ein" :errorObj="errorObj" />
    <InternalInformation
      v-if="screen === 'partnerDetail' && isSmacAdmin"
      :handleChange="handleChange"
      :hsCompanyId="hsCompanyId"
      :psmId="psmId"
      :engagementLevel="engagementLevel"
      :promoQuarterlyLimit="promoQuarterlyLimit"
      :corpStatus="corpStatus"
      :status="status"
      :serviceProvider="serviceProvider"
      :industry="industry"
      :apponlyAllowed="apponlyAllowed"
      :notificationsAllowed="notificationsAllowed"
      :monthlyHealthReport="monthlyHealthReport"
      :errorObj="errorObj"
    />
    <UpdateAccountStatusWarning
      :show="showStatusWarning"
      :close="cancelStatusChange"
      :save="updateStatus"
      :oldStatus="status"
      :newStatus="newStatus"
    />
    <ProcessOverlay :show="updating" text="Saving" />
  </div>
</template>

<script>
import partnerSettingsMixin from '@shared/mixins/partnerSettingsMixin';

import PartnerInfo from '@shared/components/partnerSettings/PartnerInfo.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';
import SaveCancelSection from '@shared/components/setting/shared/SaveCancelSection.vue';

import InternalInformation from '../../partnerSettings/InternalInformation.vue';
import UpdateAccountStatusWarning from '../../partnerSettings/UpdateAccountStatusWarning.vue';
import phoneValidation from '../../../lib/errorCatch/phoneValidation';
import emailValidation from '../../../lib/errorCatch/emailValidation';
import PartnerEin from '@shared/components/partnerSettings/PartnerEin.vue';
import einValidation from '@shared/lib/errorCatch/einValidation';

export default {
  name: 'PartnerInfoSection',
  components: {
    PartnerEin,
    PartnerInfo,
    ProcessOverlay,
    SaveCancelSection,
    InternalInformation,
    UpdateAccountStatusWarning,
  },
  mixins: [partnerSettingsMixin],
  props: {
    currentCorporation: Object,
    partnerSettings: Object,
    partnerIndustry: Array,
    updatePartnerSetting: Function,
    update: Function,
    change: Function,
    screen: String,
  },
  data() {
    const {
      corporationName = '',
      displayName = '',
      website = '',
      phone = '',
      phoneCountry = '',
      psmId = null,
      corpStatus = '',
      status = '',
      hsCompanyId,
      engagementLevel = '',
      serviceProvider,
      industry = '',
      ein = '',
    } = this.currentCorporation;
    const {
      SERVICE_REQUEST_SUPPORT_PHONE = '',
      SERVICE_REQUEST_SUPPORT_EMAIL = '',
      APPONLY_ALLOWED = false,
      NOTIFICATIONS_ALLOWED = false,
      MONTHLY_HEALTH_EMAIL = true,
      PROMO_QUARTERLY_LIMIT = 1,
    } = this.partnerSettings;
    const contact = this.contacts?.find((x) => x.contactType === 'Support');
    return {
      supportPhone: contact ? contact.phone : SERVICE_REQUEST_SUPPORT_PHONE,
      supportPhoneCo: contact ? contact.phoneCo : '',
      supportEmail: contact ? contact.email : SERVICE_REQUEST_SUPPORT_EMAIL,
      corporationName,
      displayName,
      ein,
      website,
      hsCompanyId,
      industry,
      phone,
      phoneCountry,
      errorObj: {},
      updating: false,
      psmId,
      corpStatus,
      status,
      engagementLevel,
      promoQuarterlyLimit: PROMO_QUARTERLY_LIMIT,
      serviceProvider,
      showStatusWarning: false,
      newStatus: '',
      apponlyAllowed: APPONLY_ALLOWED,
      notificationsAllowed: NOTIFICATIONS_ALLOWED,
      monthlyHealthReport: MONTHLY_HEALTH_EMAIL,
    };
  },
  computed: {
    changeCheck() {
      let changes = false;
      Object.keys(this.currentCorporation).forEach((key) => {
        if (this[key] !== undefined && this.currentCorporation[key] !== this[key]) {
          changes = true;
        }
      });
      const {
        SERVICE_REQUEST_SUPPORT_PHONE = '',
        SERVICE_REQUEST_SUPPORT_EMAIL = '',
        PROMO_QUARTERLY_LIMIT = 1,
      } = this.partnerSettings;
      const contact = this.contacts?.find((x) => x.contactType === 'Support');

      if (SERVICE_REQUEST_SUPPORT_PHONE !== this.supportPhone) {
        changes = true;
      }
      if (SERVICE_REQUEST_SUPPORT_EMAIL !== this.supportEmail) {
        changes = true;
      }
      if (PROMO_QUARTERLY_LIMIT !== this.promoQuarterlyLimit) {
        changes = true;
      }
      if (this.supportPhoneCo !== contact?.phoneCo && this.supportPhoneCo !== '') {
        changes = true;
      }
      if (!changes) {
        this.changePartnerSettingsValue('showWarningOnExit', false);
      } else if (changes && !this.showWarningOnExit && !this.warningTab) {
        this.changePartnerSettingsValue('showWarningOnExit', true);
      }
      if (
        this.apponlyAllowed !== this.partnerSettings.APPONLY_ALLOWED &&
        this.partnerSettings.APPONLY_ALLOWED !== undefined
      ) {
        changes = true;
      } else if (
        this.partnerSettings.APPONLY_ALLOWED === undefined &&
        this.apponlyAllowed !== false
      ) {
        changes = true;
      }
      if (
        this.notificationsAllowed !== this.partnerSettings.NOTIFICATIONS_ALLOWED &&
        this.partnerSettings.NOTIFICATIONS_ALLOWED !== undefined
      ) {
        changes = true;
      } else if (
        this.partnerSettings.NOTIFICATIONS_ALLOWED === undefined &&
        this.notificationsAllowed !== false
      ) {
        changes = true;
      }
      if (
        this.monthlyHealthReport !== this.partnerSettings.MONTHLY_HEALTH_EMAIL &&
        this.partnerSettings.MONTHLY_HEALTH_EMAIL !== undefined
      ) {
        changes = true;
      } else if (
        this.partnerSettings.MONTHLY_HEALTH_EMAIL === undefined &&
        this.monthlyHealthReport !== true
      ) {
        changes = true;
      }

      return changes;
    },
  },
  watch: {
    contacts(nv) {
      const contact = nv?.find((x) => x.contactType === 'Support');
      if (contact) {
        this.supportPhone = contact.phone;
        this.supportPhoneCo = contact.phoneCo;
        this.supportEmail = contact.email;
      }
    },
  },
  async mounted() {
    await this.$store.dispatch('getPartnerContacts', {
      corpId: this.currentCorporation.id,
    });
  },
  methods: {
    formatEin(value) {
      const digits = value.replace(/\D/g, '');

      if (digits.length <= 2) return digits;
      return digits.slice(0, 2) + '-' + digits.slice(2, 9);
    },
    handleChange(field, value) {
      if (field === 'partnerName') {
        this.corporationName = value;
      } else if (field === 'status') {
        this.newStatus = value;
        this.showStatusWarning = true;
      } else if (field === 'psmId' && value < 0) {
        this.psmId = null;
      } else if (field === 'ein') {
        this.ein = this.formatEin(value);
      } else {
        this[field] = value;
      }
    },
    cancelStatusChange() {
      this.showStatusWarning = false;
    },
    updateStatus() {
      this.status = this.newStatus;
      this.showStatusWarning = false;
    },
    resetPartnerConfig() {
      const {
        corporationName = '',
        displayName = '',
        ein = '',
        website = '',
        psmId = null,
        corpStatus,
        status = '',
        engagementLevel = '',
        phone = '',
        phoneCountry = '',
      } = this.currentCorporation;
      const {
        SERVICE_REQUEST_SUPPORT_PHONE = '',
        SERVICE_REQUEST_SUPPORT_EMAIL = '',
        PROMO_QUARTERLY_LIMIT = 1,
      } = this.partnerSettings;
      const contact = this.contacts?.find((x) => x.contactType === 'Support');

      this.supportEmail = SERVICE_REQUEST_SUPPORT_EMAIL;
      this.supportPhone = SERVICE_REQUEST_SUPPORT_PHONE;
      this.supportPhoneCo = contact?.phoneCo;
      this.corporationName = corporationName;
      this.displayName = displayName;
      this.ein = ein;
      this.website = website;
      this.hsCompanyId = undefined;
      this.industry = undefined;
      this.serviceProvider = undefined;
      this.errorObj = {};
      this.updating = false;
      this.psmId = psmId;
      this.corpStatus = corpStatus;
      this.status = status;
      this.engagementLevel = engagementLevel;
      this.promoQuarterlyLimit = PROMO_QUARTERLY_LIMIT;
      this.phone = phone;
      this.phoneCountry = phoneCountry;
      this.apponlyAllowed = this.partnerSettings.APPONLY_ALLOWED || false;
    },
    async updatePartnerConfig() {
      const {
        ein,
        corporationName,
        displayName,
        website,
        hsCompanyId,
        industry,
        serviceProvider,
        psmId,
        corpStatus,
        status,
        engagementLevel,
        promoQuarterlyLimit,
        phone,
        phoneCountry,
        supportEmail,
        supportPhone,
        supportPhoneCo,
      } = this;
      const errorObj = {};
      this.changePartnerSettingsValue('showWarningOnExit', false);
      if (!corporationName) {
        errorObj.partnerNameErr = 'Legal name is required.';
      }
      if (!displayName) {
        errorObj.displayNameErr = 'Display name is required.';
      }
      if (!phoneValidation(phone) || !phone) {
        errorObj.phoneErr = 'Phone number is not valid.';
      }
      if (!phoneValidation(supportPhone) || !supportPhone) {
        errorObj.supportPhoneErr = 'Phone number is not valid.';
      }
      if (!emailValidation(supportEmail)) {
        errorObj.supportEmailErr = 'Email is not valid.';
      }
      if (!einValidation(ein)) {
        errorObj.einErr = 'EIN is not valid.';
      }
      this.errorObj = errorObj;
      if (Object.keys(errorObj).length === 0) {
        this.updating = true;
        await this.update({
          corporationName,
          displayName,
          ein,
          website,
          hsCompanyId,
          industry,
          serviceProvider,
          psmId,
          corpStatus,
          status,
          engagementLevel,
          phone,
          phoneCountry,
        });
        await this.updatePartnerSetting({
          SERVICE_REQUEST_SUPPORT_EMAIL: supportEmail,
          SERVICE_REQUEST_SUPPORT_PHONE: supportPhone,
          APPONLY_ALLOWED: this.apponlyAllowed,
          PROMO_QUARTERLY_LIMIT: this.promoQuarterlyLimit,
          NOTIFICATIONS_ALLOWED: this.notificationsAllowed,
          MONTHLY_HEALTH_EMAIL: this.monthlyHealthReport,
        });
        const contact = this.contacts.find((x) => x.contactType === 'Support');
        if (!contact) {
          await this.$store.dispatch('createPartnerContact', {
            corpId: this.currentCorporation.id,
            firstName: '',
            phoneCo: supportPhoneCo,
            lastName: '',
            isPrimary: true,
            phone: supportPhone,
            email: supportEmail,
            contactType: 'Support',
          });
        } else {
          await this.$store.dispatch('editPartnerContact', {
            ...contact,
            phone: supportPhone,
            phoneCo: supportPhoneCo,
            email: supportEmail,
          });
        }
        this.updating = false;
      }
    },
  },
};
</script>
